import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
    type FictoanBadgeProps,
    type FictoanBadgeSize,
    type FictoanButtonColor,
    fictoanButtonMapping,
    type FictoanButtonProps,
    type FictoanButtonShadow,
    type FictoanButtonSize,
    type FictoanCardProps,
    fictoanComponentMapping,
    fictoanInfoPanelMapping,
    type FictoanInfoPanelProps,
    type FictoanInfoPanelShadow,
    type FictoanInfoPanelShape,
    type FictoanInfoPanelSpacing,
    type FictoanInfoPanelWidth,
    type FictoanInputFieldProps,
    type FictoanInputState,
    type FictoanTooltipPosition,
    type FictoanTooltipProps,
} from "./fictoan-schema";

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge for deduplication
 */
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Map Fictoan Button props to shadcn/ui Button props
 */
export function mapFictoanButtonProps(props: Partial<FictoanButtonProps>): {
    variant: string;
    size: string | undefined;
    disabled: boolean | undefined;
    className: string | undefined;
    loading: boolean | undefined;
    fullWidth: boolean | undefined;
} {
    const { colour, color, kind, size, shadow, isLoading, isFullWidth, disabled, ...rest } = props;

    // Determine variant (color takes precedence, then kind)
    const colorKey = (colour || color) as FictoanButtonColor;
    const kindKey = kind as keyof typeof fictoanButtonMapping.kinds;

    let variant = "default";
    if (colorKey && fictoanButtonMapping.colors[colorKey]) {
        variant = fictoanButtonMapping.colors[colorKey];
    } else if (kindKey && fictoanButtonMapping.kinds[kindKey]) {
        variant = fictoanButtonMapping.kinds[kindKey];
    }

    // Map size - handle both Fictoan sizes (tiny, small, medium, large, huge) and shadcn sizes (sm, default, lg, icon, etc.)
    const sizeKey = size as FictoanButtonSize;
    let mappedSize: string | undefined = undefined;

    if (sizeKey) {
        // First check if it's a Fictoan size that needs mapping
        if (fictoanButtonMapping.sizes[sizeKey]) {
            mappedSize = fictoanButtonMapping.sizes[sizeKey];
        } else {
            // If it's already a shadcn size (sm, lg, default, icon, icon-sm, icon-lg), use it as-is
            const validShadcnSizes = ["sm", "default", "lg", "icon", "icon-sm", "icon-lg"];
            mappedSize = validShadcnSizes.includes(sizeKey) ? sizeKey : undefined;
        }
    }

    // Handle shadow (convert to className)
    const shadowKey = shadow as FictoanButtonShadow;
    const shadowClass =
        shadowKey && fictoanButtonMapping.shadows[shadowKey] ? fictoanButtonMapping.shadows[shadowKey] : undefined;

    return {
        variant,
        size: mappedSize,
        disabled: disabled,
        className: shadowClass ? shadowClass : undefined,
        // Map Fictoan loading prop to our loading prop
        loading: isLoading,
        // Map fullWidth
        fullWidth: isFullWidth,
        ...rest,
    };
}

/**
 * Focus ring utility for accessibility
 */
export const focusRing =
    "setu:focus-visible:outline-hidden setu:focus-visible:ring-2 setu:focus-visible:ring-ring setu:focus-visible:ring-offset-2";

/**
 * Disabled state utility
 */
export const disabledState = "setu:disabled:pointer-events-none setu:disabled:opacity-50";

/**
 * Get contrast color for accessibility
 */
export function getContrastColor(bgColor: string): "light" | "dark" {
    // Simple implementation - can be enhanced with actual color contrast calculation
    const darkColors = ["black", "blue", "purple", "indigo", "green"];
    return darkColors.some((color) => bgColor.includes(color)) ? "light" : "dark";
}

/**
 * Animation utilities
 */
export const animations = {
    fadeIn: "animate-in setu:fade-in-0",
    fadeOut: "animate-out setu:fade-out-0",
    slideIn: "animate-in setu:slide-in-from-bottom-2",
    slideOut: "animate-out setu:slide-out-to-bottom-2",
    scaleIn: "animate-in setu:zoom-in-95",
    scaleOut: "animate-out setu:zoom-out-95",
} as const;

/**
 * Theme Management Utilities
 * Easy way to switch between custom themes
 */

export type ThemeName = "light" | "dark" | "setu" | "green" | "orange" | string;

/**
 * Apply a custom theme to the document
 * @param theme - Theme name (light, dark, setu, green, orange, or custom class)
 */
export function applyTheme(theme: ThemeName) {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove("light", "dark", "theme-setu", "theme-green", "theme-orange");

    // Apply new theme
    if (theme === "light") {
        // Default light theme (no class needed)
        return;
    } else if (theme === "dark") {
        root.classList.add("dark");
    } else if (["setu", "green", "orange"].includes(theme)) {
        root.classList.add(`theme-${theme}`);
    } else {
        // Custom theme class
        root.classList.add(theme);
    }
}

/**
 * Get current active theme
 */
export function getCurrentTheme(): ThemeName {
    const root = document.documentElement;

    if (root.classList.contains("dark")) return "dark";
    if (root.classList.contains("theme-setu")) return "setu";
    if (root.classList.contains("theme-green")) return "green";
    if (root.classList.contains("theme-orange")) return "orange";

    // Check for any custom theme-* class
    const customTheme = Array.from(root.classList).find((cls) => cls.startsWith("theme-"));
    if (customTheme) return customTheme.replace("theme-", "");

    return "light";
}

/**
 * React hook for theme management (if using React context)
 */
export function createThemeHook() {
    return {
        setTheme: applyTheme,
        getTheme: getCurrentTheme,
        availableThemes: ["light", "dark", "setu", "green", "orange"] as ThemeName[],
    };
}

/**
 * Map Fictoan InfoPanel props to Sheet props
 * Handles transformation from Fictoan v0.45.5 InfoPanel API to our Sheet component
 */
export function mapFictoanInfoPanelProps(props: Partial<FictoanInfoPanelProps>) {
    const {
        width,
        isOpen,
        onCloseCallback,
        bgColor,
        bgColour,
        textColor,
        textColour,
        padding,
        shadow,
        shape,
        className,
        classNames,
        ...rest
    } = props;

    // Map width to max-width classes
    const widthKey = width as FictoanInfoPanelWidth;
    const widthClass =
        widthKey && fictoanInfoPanelMapping.widths[widthKey] ? fictoanInfoPanelMapping.widths[widthKey] : undefined;

    // Map spacing
    const paddingKey = padding as FictoanInfoPanelSpacing;
    const paddingClass =
        paddingKey && fictoanInfoPanelMapping.spacing[paddingKey]
            ? fictoanInfoPanelMapping.spacing[paddingKey]
            : undefined;

    // Map shadow
    const shadowKey = shadow as FictoanInfoPanelShadow;
    const shadowClass =
        shadowKey && fictoanInfoPanelMapping.shadows[shadowKey]
            ? fictoanInfoPanelMapping.shadows[shadowKey]
            : undefined;

    // Map shape
    const shapeKey = shape as FictoanInfoPanelShape;
    const shapeClass =
        shapeKey && fictoanInfoPanelMapping.shapes[shapeKey] ? fictoanInfoPanelMapping.shapes[shapeKey] : undefined;

    // Handle background colors (basic mapping)
    const bgColorClass = bgColor || bgColour ? `setu:bg-${bgColor || bgColour}` : undefined;
    const textColorClass = textColor || textColour ? `setu:text-${textColor || textColour}` : undefined;

    // Combine all classes
    const combinedClasses = cn(
        widthClass,
        paddingClass,
        shadowClass,
        shapeClass,
        bgColorClass,
        textColorClass,
        className,
        classNames?.join(" ")
    );

    // Create onOpenChange callback that matches Sheet API
    const onOpenChange = onCloseCallback
        ? (open: boolean) => {
              if (!open) {
                  onCloseCallback();
              }
          }
        : undefined;

    return {
        // Map state props
        open: isOpen,
        onOpenChange,

        // Sheet-specific props
        className: combinedClasses || undefined,

        // Pass through remaining props
        ...rest,
    };
}

/**
 * Helper function to map common Fictoan props to Tailwind classes
 */
function mapCommonFictoanProps(props: {
    bgColor?: string;
    bgColour?: string;
    textColor?: string;
    textColour?: string;
    borderColor?: string;
    borderColour?: string;
    padding?: FictoanInfoPanelSpacing;
    margin?: FictoanInfoPanelSpacing;
    shadow?: FictoanInfoPanelShadow;
    shape?: FictoanInfoPanelShape;
    className?: string;
    classNames?: string[];
}) {
    const {
        bgColor,
        bgColour,
        textColor,
        textColour,
        borderColor,
        borderColour,
        padding,
        margin,
        shadow,
        shape,
        className,
        classNames,
    } = props;

    // Map colors
    const bgColorClass = bgColor || bgColour ? `setu:bg-${bgColor || bgColour}` : undefined;
    const textColorClass = textColor || textColour ? `setu:text-${textColor || textColour}` : undefined;
    const borderColorClass = borderColor || borderColour ? `setu:border-${borderColor || borderColour}` : undefined;

    // Map spacing
    const paddingClass =
        padding && fictoanInfoPanelMapping.spacing[padding as FictoanInfoPanelSpacing]
            ? fictoanInfoPanelMapping.spacing[padding as FictoanInfoPanelSpacing]
            : undefined;

    const marginClass =
        margin && fictoanInfoPanelMapping.spacing[margin as FictoanInfoPanelSpacing]
            ? fictoanInfoPanelMapping.spacing[margin as FictoanInfoPanelSpacing].replace("p-", "m-")
            : undefined;

    // Map shadow
    const shadowClass =
        shadow && fictoanInfoPanelMapping.shadows[shadow as FictoanInfoPanelShadow]
            ? fictoanInfoPanelMapping.shadows[shadow as FictoanInfoPanelShadow]
            : undefined;

    // Map shape
    const shapeClass =
        shape && fictoanInfoPanelMapping.shapes[shape as FictoanInfoPanelShape]
            ? fictoanInfoPanelMapping.shapes[shape as FictoanInfoPanelShape]
            : undefined;

    return cn(
        bgColorClass,
        textColorClass,
        borderColorClass,
        paddingClass,
        marginClass,
        shadowClass,
        shapeClass,
        className,
        classNames?.join(" ")
    );
}

/**
 * Map Fictoan Card props to our Card component
 */
export function mapFictoanCardProps(props: Partial<FictoanCardProps>) {
    const { children, ...commonProps } = props;

    const className = mapCommonFictoanProps(commonProps);

    return {
        className: className || undefined,
        children,
    };
}

/**
 * Map Fictoan InputField props to our Input component
 */
export function mapFictoanInputFieldProps(props: Partial<FictoanInputFieldProps>) {
    const {
        label,
        labelColour,
        type,
        placeholder,
        value,
        defaultValue,
        onChange,
        iconLeft,
        iconRight,
        helpText,
        errorText,
        validateThis,
        valid,
        invalid,
        disabled,
        required,
        ...commonProps
    } = props;

    // Determine validation state
    let validationState: FictoanInputState = "default";
    if (valid || (validateThis && !invalid)) {
        validationState = "valid";
    } else if (invalid || (validateThis && !valid)) {
        validationState = "invalid";
    }

    const baseClassName = mapCommonFictoanProps(commonProps);
    const stateClassName = fictoanComponentMapping.inputStates[validationState];

    return {
        type,
        placeholder,
        value,
        defaultValue,
        onChange,
        disabled,
        required,
        className: cn(baseClassName, stateClassName),
        // Return additional props for wrapper/label handling
        ...(label && { "data-label": label }),
        ...(labelColour && { "data-label-colour": labelColour }),
        ...(helpText && { "data-help-text": helpText }),
        ...(errorText && { "data-error-text": errorText }),
        ...(iconLeft && { "data-icon-left": iconLeft }),
        ...(iconRight && { "data-icon-right": iconRight }),
    };
}

/**
 * Map Fictoan Tooltip props to our Tooltip component
 */
export function mapFictoanTooltipProps(props: Partial<FictoanTooltipProps>) {
    const { label, position = "top", children, ...commonProps } = props;

    const className = mapCommonFictoanProps(commonProps);

    // Map position (Fictoan uses different naming than Radix)
    const mappedPosition = fictoanComponentMapping.tooltipPositions[position as FictoanTooltipPosition];

    return {
        content: label,
        side: mappedPosition,
        className: className || undefined,
        children,
    };
}

/**
 * Map Fictoan Badge props to Badge component (when implemented)
 */
export function mapFictoanBadgeProps(props: Partial<FictoanBadgeProps>) {
    const { size = "medium", children, ...commonProps } = props;

    // Map size
    const sizeClass = fictoanComponentMapping.badgeSizes[size as FictoanBadgeSize];

    const baseClassName = mapCommonFictoanProps(commonProps);

    return {
        className: cn(sizeClass, baseClassName),
        children,
    };
}
